import { ContentItem } from '../../../models/ContentManagementInterface';
import { FootNoteProps } from '../../../models/TopLevelTemplateComponentProps';
import { CarouselItemContent } from '../../template/imageCarouselTypes';
import { ListItem } from '../../template/leftRightListUtils';
import { StandardHeaderContent } from '../../template/StandardHeaderText';
import type { TableRow } from '../../template/Table';
import { htmlToTextContent } from '../../../services/htmlToTextContent';
import { normalizeListIndentation } from '../../../services/normalizeListIndentation';
import sanitizeHTML from '../../../services/sanitizeHTML';
import { headingNotSet } from '../../../services/TemplateUtils';
import type { ClipboardPayload } from './clipboardPayload';
import { extractLeadingHeadingFromHtml } from './extractLeadingHeading';
import {
  Confidence,
  gridToLeftRightItems,
  gridToStandardHeaderContent,
  gridToTextRows,
  isDelimited,
  plainTextToCarouselCaption,
  plainTextToLeftRightItems,
  plainTextToStandardHeaderContent,
  plainTextToTableRows,
} from './pastedContentHeuristics';
import { delimitedToRows } from './tableImport';
import { parseClipboardTable } from './tableModel';

/**
 * Decide what a clipboard payload means for a given block — WITHOUT AI.
 *
 * Order (structured text is checked before the image branch because Windows
 * Excel/Word put a bitmap on the clipboard *alongside* the HTML/TSV):
 *   1. HTML `<table>`        → structural (`parseClipboardTable`)
 *   2. delimited plain text  → structural (`delimitedToRows`)
 *   3. rich HTML, no table   → per-block DOM walk
 *   4. image only            → `{ kind: 'image' }` (caller may send to AI)
 *   5. plain prose           → per-block heuristic (`pastedContentHeuristics`)
 *   6. leftover image        → `{ kind: 'image' }`
 *   7. nothing usable        → `{ kind: 'empty' }`
 */

export interface PastedProps {
  content?: StandardHeaderContent['content'];
  items?: ListItem[] | CarouselItemContent[];
  rows?: TableRow[];
  footnotes?: FootNoteProps[];
}

export type PasteResult =
  | { kind: 'structured'; confidence: 'high'; properties: PastedProps; heading?: string }
  | { kind: 'plain'; confidence: Confidence; properties: PastedProps; heading?: string }
  | { kind: 'image'; file: File }
  | { kind: 'empty'; reason: string };

export const EMPTY_PASTE_REASON = 'Nothing to paste — copy some content first, then try again.';

const TABLE_HTML = /<table[\s>]/i;
const RICH_HTML = /<(p|ul|ol|li|h[1-6]|br|strong|b|em|i)[\s>]/i;

function structuredFromRows(block: ContentItem, rows: TableRow[] | null): PasteResult | null {
  if (!rows || rows.length === 0) return null;
  if (block.type === 'Table') {
    return { kind: 'structured', confidence: 'high', properties: { rows } };
  }
  const grid = gridToTextRows(rows);
  if (block.type === 'LeftRightList') {
    return {
      kind: 'structured',
      confidence: 'high',
      properties: { items: gridToLeftRightItems(grid) },
    };
  }
  if (block.type === 'StandardHeaderText') {
    return {
      kind: 'structured',
      confidence: 'high',
      properties: { content: gridToStandardHeaderContent(grid) },
    };
  }
  return null;
}

/** Visible text of a pasted HTML fragment — used as the verbatim source for an AI re-run. */
export function htmlToPlainText(html: string): string {
  if (!html) return '';
  const div = document.createElement('div');
  div.innerHTML = sanitizeHTML(html);
  return (div.textContent ?? '').replace(/\n{3,}/g, '\n\n').trim();
}

function richHtmlResult(block: ContentItem, html: string, text: string): PasteResult | null {
  if (block.type === 'StandardHeaderText') {
    const { heading, restHtml } = headingNotSet(block.properties.heading)
      ? extractLeadingHeadingFromHtml(html)
      : { heading: undefined, restHtml: html };
    return {
      kind: 'structured',
      confidence: 'high',
      properties: { content: htmlToTextContent(normalizeListIndentation(restHtml), true) },
      heading,
    };
  }
  if (block.type === 'LeftRightList') {
    const div = document.createElement('div');
    div.innerHTML = sanitizeHTML(html);
    const lines = Array.from(div.querySelectorAll('li, p'))
      .map((node) => (node.textContent ?? '').trim())
      .filter(Boolean);
    if (lines.length === 0) return null;
    return {
      kind: 'structured',
      confidence: 'high',
      properties: { items: plainTextToLeftRightItems(lines.join('\n')).items },
    };
  }
  if (block.type === 'Table') {
    const rows =
      parseClipboardTable(html, text) ?? (isDelimited(text) ? delimitedToRows(text) : null);
    return rows ? { kind: 'structured', confidence: 'high', properties: { rows } } : null;
  }
  return null;
}

function plainTextResult(block: ContentItem, text: string): PasteResult {
  if (block.type === 'Table') {
    const { rows, confidence } = plainTextToTableRows(text);
    return { kind: 'plain', confidence, properties: { rows } };
  }
  if (block.type === 'LeftRightList') {
    const { items, confidence } = plainTextToLeftRightItems(text);
    return { kind: 'plain', confidence, properties: { items } };
  }
  if (block.type === 'ImageCarousel') {
    const result = plainTextToCarouselCaption(text, block.properties);
    if ('empty' in result) return { kind: 'empty', reason: result.empty };
    return { kind: 'plain', confidence: 'low', properties: { items: result.items } };
  }
  if (block.type !== 'StandardHeaderText') {
    return { kind: 'empty', reason: EMPTY_PASTE_REASON };
  }
  const { content, heading, confidence } = plainTextToStandardHeaderContent(
    text,
    Boolean(headingNotSet(block.properties.heading)),
  );
  return { kind: 'plain', confidence, properties: { content }, heading };
}

interface NormalizedPayload {
  html: string;
  text: string;
  image: File | null;
  hasTableHtml: boolean;
  hasRichHtml: boolean;
  looksDelimited: boolean;
}

function normalizePayload(payload: ClipboardPayload): NormalizedPayload {
  const html = (payload.html ?? '').trim();
  const text = payload.text ?? '';
  return {
    html,
    text,
    image: payload.image ?? null,
    hasTableHtml: TABLE_HTML.test(html),
    hasRichHtml: html !== '' && RICH_HTML.test(html),
    looksDelimited: isDelimited(text),
  };
}

/** Steps 1–3: anything the clipboard already carries as structure — no AI, no heuristic. */
function structuredResult(block: ContentItem, p: NormalizedPayload): PasteResult | null {
  if (p.hasTableHtml) {
    const fromTable = structuredFromRows(block, parseClipboardTable(p.html, p.text));
    if (fromTable) return fromTable;
  }
  if (p.looksDelimited) {
    const fromDelimited = structuredFromRows(block, delimitedToRows(p.text));
    if (fromDelimited) return fromDelimited;
  }
  if (p.hasRichHtml) {
    const fromRich = richHtmlResult(block, p.html, p.text);
    if (fromRich) return fromRich;
  }
  return null;
}

/** The verbatim text a plain-prose heuristic should shape (image cards fall back to HTML text). */
function plainSource(block: ContentItem, p: NormalizedPayload): string {
  if (p.text.trim() !== '') return p.text;
  if (block.type === 'ImageCarousel') return htmlToPlainText(p.html);
  return '';
}

export function parsePastedContent(block: ContentItem, payload: ClipboardPayload): PasteResult {
  const p = normalizePayload(payload);

  const structured = structuredResult(block, p);
  if (structured) return structured;

  const hasUsableStructure = p.hasTableHtml || p.hasRichHtml || p.looksDelimited;
  if (p.image && !hasUsableStructure && p.text.trim() === '') {
    return { kind: 'image', file: p.image };
  }

  const effectiveText = plainSource(block, p);
  if (effectiveText.trim() !== '') return plainTextResult(block, effectiveText);

  if (p.image) return { kind: 'image', file: p.image };
  return { kind: 'empty', reason: EMPTY_PASTE_REASON };
}
